'use client';

import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useUnauthorizedFees, useUserProfile } from '@/lib/api/auth/hooks';
import type { Fees } from '@/lib/api/auth/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';

interface FeesContextValue {
  fees: Fees | null;
  isLoading: boolean;
  error: Error | null;
}

interface FeesContextInternal extends FeesContextValue {
  /** Marks that a consumer is mounted and the fees should actually be fetched. */
  request: () => void;
}

const FeesContext = createContext<FeesContextInternal | undefined>(undefined);

/**
 * Fees are read by the order form and nowhere else, yet the provider sits in
 * the root tree of every zone. Nothing is fetched until a `useFees()` consumer
 * mounts, so pages without trading UI (home, news, account) make neither the
 * anonymous fees call nor the profile call on the provider's behalf.
 */
export function FeesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuthContext();
  const [requested, setRequested] = useState(false);
  const request = useCallback(() => setRequested(true), []);

  // Fetch unauthorized fees for non-authenticated users
  const {
    data: unauthorizedFees,
    isLoading: isLoadingUnauthorizedFees,
    error: unauthorizedFeesError,
  } = useUnauthorizedFees({
    enabled: requested && !isAuthLoading && !isAuthenticated,
  });

  // Fetch user profile (includes fees) for authenticated users
  const {
    data: userProfile,
    isLoading: isLoadingUserProfile,
    error: userProfileError,
  } = useUserProfile({
    enabled: requested && !isAuthLoading && isAuthenticated,
  });

  // Determine which fees to use based on authentication status. Until a
  // consumer has asked, the fees count as loading rather than absent, so the
  // first render of an order form never sees a "no fees" state.
  const fees = isAuthenticated ? (userProfile?.fees ?? null) : (unauthorizedFees ?? null);
  const isLoading =
    !requested || (isAuthenticated ? isLoadingUserProfile : isLoadingUnauthorizedFees);
  const error = isAuthenticated ? userProfileError : unauthorizedFeesError;

  return (
    <FeesContext.Provider value={{ fees, isLoading, error, request }}>
      {children}
    </FeesContext.Provider>
  );
}

export function useFees(): FeesContextValue {
  const context = useContext(FeesContext);
  if (!context) {
    throw new Error('useFees must be used within FeesProvider');
  }
  const { request, ...value } = context;
  useEffect(() => {
    request();
  }, [request]);
  return value;
}
